import { createFileRoute, notFound } from '@tanstack/react-router';
import type { ImagePromptDetail } from '@/api/image-prompts';
import { PromptDetailRoute } from '@/components/custom/prompt-detail-route';
import { websiteConfig } from '@/config/website';
import { m } from '@/locale/paraglide/messages';
import { seo } from '@/lib/seo';

const PROMPT_MODEL_LABELS: Record<string, string> = {
  gptimage: 'GPT Image',
  nanobanana: 'Nano Banana',
};

function buildPromptMetaTitle(prompt: ImagePromptDetail): string {
  const siteName = websiteConfig.metadata?.name ?? '';
  const modelLabel = PROMPT_MODEL_LABELS[prompt.model];
  const primaryCategory = prompt.categories[0]?.title;

  const pageLabel = [m.prompt_detail_meta_label(), primaryCategory, modelLabel]
    .filter(Boolean)
    .join(' · ');

  return siteName ? `${pageLabel} | ${siteName}` : pageLabel;
}

export const Route = createFileRoute('/prompt/$id')({
  loader: async ({ params, context }) => {
    const id = Number(params.id);
    if (!Number.isFinite(id) || id <= 0) throw notFound();
    const { getImagePromptById } = await import('@/api/image-prompts');
    const prompt = await getImagePromptById({ data: { id } });
    if (!prompt) throw notFound();
    // Seed React Query cache so the image editor page's useQuery hits instantly.
    context.queryClient.setQueryData(['imagePromptDetail', id], prompt);
    return prompt;
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const title = buildPromptMetaTitle(loaderData);
    const description = loaderData.prompt.slice(0, 160);
    return seo(`/prompt/${loaderData.id}`, {
      title,
      description,
      image: loaderData.coverUrl,
      type: 'article',
    });
  },
  component: PromptDetailRoute,
});
